import { MenuItem, Stack, TextField, Typography } from '@mui/material'
import {
  ShippingCountries,
  ShippingCountryCodes,
  ShippingProvincesCA,
  ShippingStatesUS,
  isAuthenticated,
  splitEmailAddresses,
} from 'jms-domain'
import { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { match } from 'ts-pattern'

import { TextInput } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'

export const FINALIZE_FORM_ID = 'finalize-form'

export interface FinalizeFormValues {
  firstName: string
  lastName: string
  customerEmailAddresses: string
  dealerEmailAddresses: string
  companyName: string
  projectName: string
  shippingAddressStreet: string
  shippingAddressCity: string
  shippingAddressState: string
  shippingAddressZipCode: string
  shippingAddressCountryCode: string
}

interface FinalizeFormProps {
  isPending: boolean
  onSubmit: (data: FinalizeFormValues) => void
}

const validateEmails = (emailsString: string) => {
  if (emailsString.trim() === '') {
    return true
  }

  const emails = splitEmailAddresses(emailsString)

  for (const email of emails) {
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return `Invalid email address: ${email}`
    }
  }

  return true
}

export const FinalizeForm = (props: FinalizeFormProps) => {
  const { isPending, onSubmit } = props
  const session = configuratorInterface.useSession()

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FinalizeFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      customerEmailAddresses: '',
      dealerEmailAddresses: session.sessionPayload?.data.emailAddress ?? '',
      companyName: '',
      projectName: '',
      shippingAddressStreet: '',
      shippingAddressCity: '',
      shippingAddressState: '',
      shippingAddressZipCode: '',
      shippingAddressCountryCode: ShippingCountryCodes.USA,
    },
  })

  const onSubmitInternal = useCallback(
    (data: FinalizeFormValues) => {
      onSubmit(data)
    },
    [onSubmit]
  )

  // Get shippingAddressCountryCode from form's state
  const shippingAddressCountryCode = watch('shippingAddressCountryCode')
  const states = match(shippingAddressCountryCode)
    .with(ShippingCountryCodes.USA, () => ShippingStatesUS)
    .with(ShippingCountryCodes.CAN, () => ShippingProvincesCA)
    // React Hook Form or MUI sometimes really return undefined
    // even when there's default value set and no manual changes were made.
    .otherwise(() => ShippingStatesUS)

  return (
    <form onSubmit={handleSubmit(onSubmitInternal)} id={FINALIZE_FORM_ID} noValidate>
      <Stack spacing={2} sx={{ ml: 1, mr: 1 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Finalize order
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please provide your information below to receive the configuration details, links to share, PDF and generated
          image.
        </Typography>
        <Stack spacing={2} direction="row">
          <Controller
            name="firstName"
            control={control}
            disabled={isPending}
            rules={{ required: 'First name is required' }}
            render={({ field }) => (
              <TextInput
                {...field}
                placeholder="First name*"
                hasError={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            disabled={isPending}
            rules={{ required: 'Last name is required' }}
            render={({ field }) => (
              <TextInput
                {...field}
                placeholder="Last name*"
                hasError={!!errors.lastName}
                helperText={errors.lastName?.message}
              />
            )}
          />
        </Stack>
        {isAuthenticated(session) && (
          <Controller
            name="dealerEmailAddresses"
            control={control}
            disabled={isPending}
            render={({ field }) => (
              <TextInput
                {...field}
                placeholder="Dealer email address"
                hasError={!!errors.dealerEmailAddresses}
                helperText={errors.dealerEmailAddresses?.message}
              />
            )}
            rules={{
              validate: validateEmails,
            }}
          />
        )}
        <Controller
          name="customerEmailAddresses"
          control={control}
          disabled={isPending}
          rules={{
            required: 'Customer email address is required',
            validate: validateEmails,
          }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Customer email address*"
              hasError={!!errors.customerEmailAddresses}
              helperText={errors.customerEmailAddresses?.message}
            />
          )}
        />
        <Controller
          name="companyName"
          control={control}
          disabled={isPending}
          rules={{ required: 'Company name is required' }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Company name*"
              hasError={!!errors.companyName}
              helperText={errors.companyName?.message}
            />
          )}
        />
        <Controller
          name="projectName"
          control={control}
          disabled={isPending}
          rules={{ required: 'Project is required' }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Project*"
              hasError={!!errors.projectName}
              helperText={errors.projectName?.message}
            />
          )}
        />
        <Controller
          name="shippingAddressCountryCode"
          control={control}
          disabled={isPending}
          rules={{ required: 'Country is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="Country*"
              error={!!errors.shippingAddressCountryCode}
              helperText={errors.shippingAddressCountryCode?.message}
            >
              <MenuItem value={ShippingCountryCodes.USA}>{ShippingCountries[ShippingCountryCodes.USA]}</MenuItem>
              <MenuItem value={ShippingCountryCodes.CAN}>{ShippingCountries[ShippingCountryCodes.CAN]}</MenuItem>
            </TextField>
          )}
        />
        <Controller
          name="shippingAddressState"
          control={control}
          disabled={isPending}
          rules={{ required: 'State is required' }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              label="State or province*"
              error={!!errors.shippingAddressState}
              helperText={errors.shippingAddressState?.message}
            >
              {Object.entries(states).map(([code, name]) => (
                <MenuItem key={code} value={code}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name="shippingAddressStreet"
          control={control}
          disabled={isPending}
          rules={{ required: 'Street is required' }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Street*"
              hasError={!!errors.shippingAddressStreet}
              helperText={errors.shippingAddressStreet?.message}
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <Controller
            name="shippingAddressCity"
            control={control}
            disabled={isPending}
            rules={{ required: 'City is required' }}
            render={({ field }) => (
              <TextInput
                {...field}
                placeholder="City*"
                hasError={!!errors.shippingAddressCity}
                helperText={errors.shippingAddressCity?.message}
              />
            )}
          />
          <Controller
            name="shippingAddressZipCode"
            control={control}
            disabled={isPending}
            rules={{ required: 'Zip code is required' }}
            render={({ field }) => (
              <TextInput
                {...field}
                placeholder="Zip code*"
                hasError={!!errors.shippingAddressZipCode}
                helperText={errors.shippingAddressZipCode?.message}
              />
            )}
          />
        </Stack>
      </Stack>
    </form>
  )
}
